
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { RestaurantProfile } from '@/components/settings/RestaurantProfile';
import { SocialConnections } from '@/components/settings/SocialConnections';
import { AssistantRequests } from '@/components/settings/AssistantRequests';
import { AdminPanel } from '@/components/settings/AdminPanel';
import { useAdmin } from '@/contexts/AdminContext';

const Settings = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAdmin();
  
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-semibold tracking-tight">{t('nav.settings')}</h1>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="flex flex-wrap mb-4">
          <TabsTrigger value="profile">{t('settings.profile')}</TabsTrigger>
          <TabsTrigger value="restaurant">{t('settings.restaurant')}</TabsTrigger>
          <TabsTrigger value="preferences">{t('settings.preferences')}</TabsTrigger>
          <TabsTrigger value="integrations">{t('settings.integrations')}</TabsTrigger>
          <TabsTrigger value="assistant">{t('settings.assistantRequests')}</TabsTrigger>
          {isAdmin && <TabsTrigger value="admin">{t('settings.admin.admin')}</TabsTrigger>}
        </TabsList>
        
        <TabsContent value="profile" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.personalInfo')}</CardTitle>
              <CardDescription>
                {t('settings.updatePersonalDetails')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">{t('settings.firstName')}</Label>
                    <Input id="firstName" defaultValue="Demo" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">{t('settings.lastName')}</Label>
                    <Input id="lastName" defaultValue="User" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">{t('settings.email')}</Label>
                  <Input id="email" type="email" defaultValue="demo@example.com" />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company">{t('settings.company')}</Label>
                  <Input id="company" defaultValue="Demo Restaurant" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="position">{t('settings.position')}</Label>
                  <Input id="position" defaultValue="Restaurant Manager" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('settings.phone')}</Label>
                  <Input id="phone" type="tel" defaultValue="+1 (555) 123-4567" />
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button>{t('settings.saveChanges')}</Button>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.security')}</CardTitle>
              <CardDescription>
                {t('settings.updateSecuritySettings')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">{t('settings.currentPassword')}</Label>
                  <Input id="currentPassword" type="password" />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">{t('settings.newPassword')}</Label>
                    <Input id="newPassword" type="password" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{t('settings.confirmPassword')}</Label>
                    <Input id="confirmPassword" type="password" />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button>{t('settings.updatePassword')}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="restaurant" className="mt-6 space-y-6">
          <RestaurantProfile />
          <SocialConnections />
        </TabsContent>
        
        <TabsContent value="preferences" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.appearance')}</CardTitle>
              <CardDescription>
                {t('settings.customizeAppearance')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="darkMode">{t('settings.darkMode')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.enableDarkMode')}
                    </p>
                  </div>
                  <Switch id="darkMode" />
                </div>
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="compactView">{t('settings.compactView')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.enableCompactView')}
                    </p>
                  </div>
                  <Switch id="compactView" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.notifications')}</CardTitle>
              <CardDescription>
                {t('settings.manageNotifications')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="priceAlerts">{t('settings.priceAlerts')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.enablePriceAlerts')}
                    </p>
                  </div>
                  <Switch id="priceAlerts" defaultChecked />
                </div>
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="recipeUpdates">{t('settings.recipeUpdates')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.enableRecipeUpdates')}
                    </p>
                  </div>
                  <Switch id="recipeUpdates" defaultChecked />
                </div>
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="weeklyReports">{t('settings.weeklyReports')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.enableWeeklyReports')}
                    </p>
                  </div>
                  <Switch id="weeklyReports" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="integrations" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>API Connections</CardTitle>
              <CardDescription>
                Manage your external API connections
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Label>Supplier API</Label>
                      <Badge variant="outline" className="text-xs">Connected</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Connect to your supplier's pricing API
                    </p>
                  </div>
                  <Button variant="outline" size="sm">Manage</Button>
                </div>
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Label>POS System</Label>
                      <Badge variant="outline" className="text-xs bg-muted/30">Not Connected</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Integrate with your point of sale system
                    </p>
                  </div>
                  <Button variant="outline" size="sm">Connect</Button>
                </div>
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Label>Accounting Software</Label>
                      <Badge variant="outline" className="text-xs bg-muted/30">Not Connected</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Connect to your accounting software
                    </p>
                  </div>
                  <Button variant="outline" size="sm">Connect</Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Export & Import</CardTitle>
              <CardDescription>
                Manage your data export and import options
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Export Data</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Export your recipes and cost data
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">CSV</Button>
                      <Button variant="outline" size="sm">JSON</Button>
                      <Button variant="outline" size="sm">PDF</Button>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Import Data</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Import recipes and cost data
                    </p>
                    <Button>Upload File</Button>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assistant" className="mt-6 space-y-6">
          <AssistantRequests />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="admin" className="mt-6 space-y-6">
            <AdminPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Settings;
